import { faCopy, faEdit, faTrashAlt } from '@fortawesome/free-regular-svg-icons'
import {
  faArchive,
  faCheckCircle,
  faFileExport,
  faPencilAlt,
  faQrcode,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CertificateDocumentStatus } from '../../../../../enums/CertificateDocumentStatus'
import { SchemeEcmType } from '../../../../../enums/SchemeEcmType'
import {
  updateCertificateAction,
  updateCertificateScheme,
  updateLineDetails,
  updateModelList,
  updateOrderLineNumbers,
  updatePartySiteNumber,
  updateProdDetailsModelList,
  updateSelectedOrderLineNumber,
  updateSelectedProjectDetails,
  updateServiceRequestNumber,
} from '../../../../../features/createCertificate/createCertificateSlice'
import useToastr from '../../../../../hooks/useToastr'
import CertificateListService from '../../../../../services/CertificateListService'
import ProjectDetailsService from '../../../../../services/ProjectDetailsService'
import CopyCertificateModal from '../../../../../shared/copy-certificate-modal/CopyCertificateModal'
import GridActionPopover from '../../../../../shared/grid-action-popover/GridActionPopover'
import WithdrawCertificateModal from '../../../../../shared/withdraw-certificate-modal/WithdrawCertificateModal'
import { flexProjectContactRole } from '../../../../../utils/constants/flex-project-contact-role.constants'
import AddNewCertificate from '../../add-new-certificate/AddNewCertificate'
import { AddAttachmentsRPUtils } from '../../add-new-certificate/rp-library/shared/utils/AddAttachmentsRPUtils'
import GenerateDocumentModal from '../../certificate-details/generate-document-modal/GenerateDocumentModal'
import useGenerateDocument from '../../certificate-details/generate-document-modal/useGenerateDocument'
import classes from './GridAction.module.scss'
import { useCertificateQrCodeGenerator } from '../../../hooks/useCertificateQrCodeGenerator'

const GridAction = ({
  setLoading,
  childPopOverRef,
  selectedData,
  onActivateCertificate,
  setShowCertificateDetails,
  onDeleteCertificate,
  isReadOnly,
}) => {
  const { showInfo } = useToastr()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [isDisable, setIsDisable] = useState(false)
  const [details, setDetails] = useState(null)
  const [displayAddNewCertModal, setDisplayAddNewCertModal] = useState(false)
  const [actionList, setActionList] = useState([])
  const [isModifyCert, setIsModifyCert] = useState(false)
  const [isOpenCopy, setIsOpenCopy] = useState(false)
  const [certificateId, setCertificateId] = useState('')
  const [certificateName, setCertificateName] = useState('')
  const [revisionNumber, setRevisionNumber] = useState('')
  const [hasRevisionNumber, setHasRevisionNumber] = useState(false)
  const [modalHeader, setModalHeader] = useState(null)
  const [attachments, setAttachments] = useState(null)
  const [rpAttachments, setRPAttachments] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const generateDocumentHook = useGenerateDocument(selectedData)
  const qrCodeGenerator = useCertificateQrCodeGenerator(selectedData)

  const hasPendingTestReportFailedMessage =
    "Please finish linking all documents in this certificate's flyout window or in the Pending Documents List page before attempting to modify this certificate."

  const openCertificationInNewtab = useCallback(() => {
    window
      .open(
        `${process.env.REACT_APP_WORKBENCH_CERTIFICATION_URL}/certification/details?id=${selectedData.certificateId}`,
        '_blank'
      )
      .focus()
  }, [selectedData])
  const openCertificateAction = {
    textDisplay: 'Open Certificate in Workbench',
    action: openCertificationInNewtab,
    icon: faUpRightFromSquare,
  }

  const generateDocumentAction = {
    textDisplay: 'Generate Document',
    action: () => generateDocumentHook.show(),
    icon: faFileExport,
  }

  const generateQrCodeAction = {
    textDisplay: 'Generate QR Code',
    action: () => qrCodeGenerator.downloadZip(),
    icon: faQrcode,
    isHide: !qrCodeGenerator.isSchemeSupported,
    isDisabled: qrCodeGenerator.isDisabled,
  }

  const withdrawCertificationInNewtab = useCallback(() => {
    let withdrawInWBLink = `/results?exactSearch=false&advancedSearch=false&certificateName=${selectedData.certificateName}&sort=-updatedOn`
    window
      .open(`${process.env.REACT_APP_WORKBENCH_CERTIFICATION_URL}${withdrawInWBLink}`, '_blank')
      .focus()
  }, [selectedData])

  useEffect(async () => {
    setIsLoading(true)
    if (qrCodeGenerator.isSupportedSchemesLoading) return

    let newActionsList = []

    const result = await CertificateListService.getCertificateSchemeDetailByName(
      selectedData.certificateType
    )
    const isCO = result?.data?.isCo ?? false

    const withdrawTextDisplay = isCO ? 'Withdraw Certificate in Workbench' : 'Withdraw'

    if (selectedData?.certificateStatus == 'Active') {
      newActionsList = [
        {
          textDisplay: 'Modify',
          icon: faPencilAlt,
          action: modifyCertificate,
        },
        { textDisplay: 'Create Copy', icon: faCopy, action: copyCertificate },
        {
          textDisplay: 'Withdraw',
          icon: faArchive,
          action: withdrawCertificateV2,
        },
        generateDocumentAction,
        generateQrCodeAction,
        openCertificateAction,
      ]
    }

    if (selectedData?.certificateStatus == 'Under Revision') {
      newActionsList = [
        { textDisplay: 'Edit', icon: faEdit, action: editCertificate },
        {
          textDisplay: 'Activate',
          icon: faCheckCircle,
          action: activateCertificate,
          isDisabled: true,
          isHide: true,
        },
        {
          textDisplay: 'Create Copy',
          icon: faCopy,
          action: copyCertificate,
        },
        generateDocumentAction,
        generateQrCodeAction,
        openCertificateAction,
      ]

      if (!isReadOnly) {
        newActionsList.push({
          textDisplay: 'Delete',
          icon: faTrashAlt,
          action: () => onDeleteCertificate(selectedData),
        })
      }
    }

    if (
      selectedData?.certificateStatus == 'Withdrawn' ||
      selectedData?.certificateStatus == 'Obsolete'
    ) {
      newActionsList = [
        { textDisplay: 'Create Copy', icon: faCopy, action: copyCertificate },
        generateQrCodeAction,
      ]
    }

    setActionList(newActionsList)
    setIsLoading(false)
  }, [selectedData, qrCodeGenerator.isSupportedSchemesLoading])

  const dialogFuncMap = {
    displayAddNewCertModal: setDisplayAddNewCertModal,
  }

  const onShowModal = name => {
    dialogFuncMap[`${name}`](true)
  }

  const activateCertificate = async () => {
    setIsDisable(true)
    await onActivateCertificate(selectedData)
    setIsDisable(false)
  }

  const withdrawCertificate = async () => {
    setLoading(true)
    const result = await CertificateListService.getCertificateAttributes(
      selectedData.certificateType,
      ''
    )

    if (result[0]) {
      const certificateAttributes = result[0]['cert_attributes']

      if (certificateAttributes) {
        const revision = certificateAttributes.find(a => a.attribute_name === 'revisionNumber')

        if (revision) {
          setHasRevisionNumber(true)
        } else {
          setHasRevisionNumber(false)
        }
      }
    }

    setCertificateId(selectedData.certificateId)
    setCertificateName(selectedData.certificateName)
    setRevisionNumber(selectedData.revisionNumber)
    setIsOpen(true)
    setLoading(false)
  }

  const withdrawCertificateV2 = async () => {
    setLoading(true)
    setIsModifyCert(true)

    dispatch(
      updateCertificateAction({
        isWithdraw: true,
      })
    )

    await loadDetails()
    setModalHeader({
      title: 'Withdraw Certificate',
    })

    setLoading(false)
  }

  const editCertificate = async () => {
    setIsModifyCert(false)
    var hasPendingTestReport = await loadDetails()
    if (hasPendingTestReport) {
      showInfo(
        'Continue Linking of Documents before Editing Certificate',
        hasPendingTestReportFailedMessage
      )
    } else {
      setModalHeader({
        title: 'Edit Certificate',
        subTitle: "Continue working on a Certificate saved as 'Under Revision', or as a draft.",
      })
    }
  }

  const modifyCertificate = async () => {
    setIsModifyCert(true)
    var hasPendingTestReport = await loadDetails()

    if (hasPendingTestReport) {
      showInfo(
        'Continue Linking of Documents before Editing Certificate',
        hasPendingTestReportFailedMessage
      )
    } else {
      setModalHeader({
        title: 'Modify Certificate',
        subTitle: 'Create a new revision of an existing certificate.',
      })
    }
  }

  const copyCertificate = async () => {
    setIsDisable(true)
    setLoading(true)

    const detail = await CertificateListService.getCertificateDetails(selectedData.certificateId)
    detail.certificateId = selectedData.certificateId
    detail.localData = detail.localData && JSON.parse(detail?.localData)[0]
    ifECMClearTheOldAttachmentImplementation(detail?.localData)
    setDetails(detail)
    setIsOpenCopy(true)

    dispatch(updatePartySiteNumber(selectedData.ownerReference))
    setIsDisable(false)
    setLoading(false)
  }

  const loadDetails = async () => {
    setIsDisable(true)
    setLoading(true)
    setRevisionNumber(selectedData.revisionNumber)

    let detail
    let certificateDetailFromE2E

    await Promise.allSettled([
      CertificateListService.getCertificateDetails(selectedData.certificateId).then(response => {
        detail = response
        dispatch(updateServiceRequestNumber(detail?.certificateDetails?.serviceRequestNumber))
      }),
      CertificateListService.getCertificateDetailByWorkbenchCertificateId(
        selectedData.certificateId
      ).then(response => {
        certificateDetailFromE2E = response
      }),
    ])

    await CertificateListService.syncDocument(selectedData.uniqueCertificateId)
    let hasPendingTestReport = false

    if (certificateDetailFromE2E.data?.baseModel) {
      detail?.assets?.forEach(asset => {
        if (asset.modelName == certificateDetailFromE2E.data.baseModel) {
          asset.isBaseModel = true
          return
        }
      })
    }

    if (
      certificateDetailFromE2E?.data?.schemeEcmType &&
      certificateDetailFromE2E?.data?.schemeEcmType === SchemeEcmType.RP
    ) {
      hasPendingTestReport = false
      await getRPAttachments(certificateDetailFromE2E?.data?.workbenchUniqueCertificateId)
    } else {
      var attachments = await getAttachments(
        certificateDetailFromE2E?.data?.workbenchUniqueCertificateId
      )

      if (
        attachments?.filter(
          attachment =>
            attachment.status == CertificateDocumentStatus.TestReportDuplicateLinkToEcmFailed
        ).length > 0
      ) {
        hasPendingTestReport = true
      }

      if (hasPendingTestReport) {
        setIsDisable(false)
        setLoading(false)

        return hasPendingTestReport
      }
    }

    detail.certificateId = selectedData.certificateId
    detail.localData = detail.localData && JSON.parse(detail.localData)[0]
    ifECMClearTheOldAttachmentImplementation(detail.localData)
    setDetails(detail)

    await populateProjectDetails(detail?.projectNumber ?? detail?.localData?.project_number, detail?.localData?.line_number)

    dialogFuncMap['displayAddNewCertModal'](true)

    dispatch(updateCertificateScheme(detail.certificateScheme))
    dispatch(updateProdDetailsModelList(detail.assets))
    dispatch(updateModelList(detail.assets))
    dispatch(updatePartySiteNumber(selectedData.ownerReference))
    setIsDisable(false)
    setLoading(false)

    return hasPendingTestReport
  }
  const ifECMClearTheOldAttachmentImplementation = data => {
    if (data && data.attachments) data.attachments = null
  }
  const getAttachments = async uniqueCertificateId => {
    if (!uniqueCertificateId) return
    const attachments =
      await CertificateListService.getCertificateDocumentsByUniqueCertificateId(uniqueCertificateId)
    setAttachments(attachments?.data)
    return attachments?.data
  }

  const getRPAttachments = async uniqueCertificateId => {
    if (!uniqueCertificateId) return

    const rpAttachments =
      await CertificateListService.getCertificateRPDocumentsByUniqueCertificateId(
        uniqueCertificateId
      )

    const transformData = AddAttachmentsRPUtils.mapCertificateAttachmentsToRPModel(
      rpAttachments?.data
    )

    setRPAttachments(transformData)
  }

  const populateProjectDetails = async (projectNumber, lineNumber) => {
    const projectNumbers = await ProjectDetailsService.getProjectSuggestions(projectNumber)

    if (projectNumbers.length === 0) return

    let updatedSelectedProjectDetails

    const item = projectNumbers[0]
    const data = await ProjectDetailsService.getProjectDetail(item.project_Number)

    let tempLineDetails = []
    data.value.forEach(d => {
      //Filter - Get Child Lines
      const lineDatas = d.projectServiceLines.filter(l => l.lineNumber.split('.').length === 3)

      if (lineDatas.length === 0) {
        return
      }

      for (var i = 0; i < lineDatas.length; i++) {
        const projectContacts = getProjectContact(d.projectContacts)

        lineDatas[i].partySiteNumber = projectContacts.partySiteNumber
        lineDatas[i].companyId = d.companyId
        lineDatas[i].address = projectContacts.address
        lineDatas[i].city = projectContacts.city
        lineDatas[i].state = projectContacts.state
        lineDatas[i].province = projectContacts.province
        lineDatas[i].country = projectContacts.country
        lineDatas[i].postalcode = projectContacts.postalCode
        lineDatas[i].partysite = projectContacts.partySiteNumber
        lineDatas[i].customer = d.companyName
        lineDatas[i].poc = projectContacts.country
        lineDatas[i].handlerName = d.projectHandler
        lineDatas[i].fulfillmentLocation = item.preferred_Fulfillment_Location
        lineDatas[i].projectFlexId = d.projectId
        lineDatas[i].projectNumber = d.projectNumber
        lineDatas[i].orderNumber = d.orderNumber
        lineDatas[i].quoteNo = d.quoteNo
        lineDatas[i].projectName = d.name
      }

      tempLineDetails = tempLineDetails.concat(lineDatas)
    })
    tempLineDetails = tempLineDetails.filter(l => l != null)

    const lineNumbers = tempLineDetails.map(d => d.lineNumber)
    const sortedLineNumbers = lineNumbers
      .map(a =>
        a
          .split('.')
          .map(n => +n + 100000)
          .join('.')
      )
      .sort()
      .map(a =>
        a
          .split('.')
          .map(n => +n - 100000)
          .join('.')
      )

    const mappedLines = sortedLineNumbers.map(d => {
      var details = tempLineDetails.find(l => l.lineNumber === d)
      return {
        name: (details?.isCpq ? details?.parentLineNumber : d) + ' - ' + details?.projectName,
        value: d,
      }
    })

    const selectedProjectDetails = tempLineDetails.find(t => t.lineNumber === lineNumber)

    if (selectedProjectDetails) {
      updatedSelectedProjectDetails = await getUserDetails(selectedProjectDetails)
    } else {
      const tempLineDetailData = tempLineDetails.find(
        line => line.lineNumber === mappedLines[0].value
      )

      updatedSelectedProjectDetails = await getUserDetails(tempLineDetailData)
    }

    await getServiceLine(updatedSelectedProjectDetails)

    dispatch(updateLineDetails(tempLineDetails))
    dispatch(updateOrderLineNumbers(mappedLines))
    dispatch(updateSelectedProjectDetails(updatedSelectedProjectDetails))
    dispatch(updateSelectedOrderLineNumber(lineNumber ?? mappedLines[0].value))
  }

  const getServiceLine = async selectedDetailsToUpdate => {
    const result = await ProjectDetailsService.getServiceLine(selectedDetailsToUpdate.projectId)
    selectedDetailsToUpdate.serviceLine = result.service_Line
  }

  const getUserDetails = async selectedProjectDetails => {
    if (!selectedProjectDetails || !selectedProjectDetails.handlerName)
      return selectedProjectDetails

    const response = await ProjectDetailsService.getUserDetails(selectedProjectDetails.handlerName)
    if (!response) return selectedProjectDetails

    const handlerCountry = response.split('-')[0]
    const handlerLocation = response.split('-')[1]

    selectedProjectDetails.handlerCountry = handlerCountry
    selectedProjectDetails.handlerLocation = handlerLocation
    return selectedProjectDetails
  }

  const getProjectContact = projectContacts => {
    const soldTo = projectContacts.find(pc => pc.contactRoleId === flexProjectContactRole.SoldTo)
    if (soldTo?.partySiteNumber) return soldTo

    const shipTo = projectContacts.find(pc => pc.contactRoleId === flexProjectContactRole.ShipTo)
    if (shipTo?.partySiteNumber) return shipTo

    const customer = projectContacts.find(
      pc => pc.contactRoleId === flexProjectContactRole.Customer
    )
    if (customer?.partySiteNumber) return customer

    const billTo = projectContacts.find(pc => pc.contactRoleId === flexProjectContactRole.BillTo)
    if (billTo?.partySiteNumber) return billTo
  }

  return (
    <div className={classes['grid-action']}>
      <GridActionPopover
        ref={childPopOverRef}
        actionList={actionList}
        isDisable={isDisable}
        isLoading={isLoading}
      ></GridActionPopover>

      <AddNewCertificate
        dialogFuncMap={dialogFuncMap}
        displayAddNewCertModal={displayAddNewCertModal}
        isEditCert={true}
        isModifyCert={isModifyCert}
        setIsModifyCert={setIsModifyCert}
        certificateId={details?.certificateId}
        certificateScheme={details?.certificateScheme}
        revisionNumber={revisionNumber}
        editData={details?.editData}
        localData={details?.localData}
        certificateStatus={details?.certificateStatus}
        hasRevisions={details?.revisionHistory.length > 0}
        setShowCertificateDetails={setShowCertificateDetails}
        customModalHeader={
          Boolean(modalHeader) ? (
            <>
              <div>{modalHeader.title}</div>
              <small
                style={{
                  fontWeight: 'normal',
                }}
              >
                {modalHeader.subTitle}
              </small>
            </>
          ) : undefined
        }
        hierarchyId={details?.hierarchyId}
        inputAttachments={attachments}
        setAttachments={setAttachments}
        rpAttachments={rpAttachments}
        setRPAttachments={setRPAttachments}
        uniqueCertificateId={details?.uniqueCertificateId}
      />
      <WithdrawCertificateModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        certificateId={certificateId}
        hasRevisionNumber={hasRevisionNumber}
        revisionNumber={revisionNumber}
      />
      <CopyCertificateModal
        isOpen={isOpenCopy}
        setIsOpen={setIsOpenCopy}
        details={details}
        setShowCertificateDetails={setShowCertificateDetails}
      />
      <GenerateDocumentModal hook={generateDocumentHook} />
    </div>
  )
}

export default GridAction
